import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut, signInWithEmailAndPassword, signInWithCustomToken } from "../Utils/firebaseConfig";
import { firebaseService } from "../Utils/firebaseService";
import isElectron from '../Utils/isElectron';
import { toast } from '@mobiscroll/react';

const AuthContext = createContext();

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // All original state variables
  const [user, setUser] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gCalAccessToken, setGcalAccessToken] = useState(null);
  const [gCalRefreshToken, setGcalRefreshToken] = useState(null);
  const [userCalendars, setUserCalendars] = useState([]);
  const [userSelectedCalendar, setUserSelectedCalendar] = useState("");

  // --- STABLE USEEFFECT HOOKS ---

  // EFFECT 1: Manages ONLY the Firebase authentication state
  useEffect(() => {
    // Unified auth state listener for both web and Electron
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // EFFECT 2: Manages fetching user data from Firestore AFTER authentication
  useEffect(() => {
    // This effect runs only when the `user` object changes (e.g., from null to a user)
    if (user) {
      // Inform Electron main of the signed-in identity (used for per-user notification filters)
      try {
        const fallbackEmail = (typeof window !== 'undefined') ? localStorage.getItem('electronEmail') : null;
        const identityEmail = user.email || fallbackEmail;
        if (isElectron() && window.electronAPI?.setUserIdentity && (identityEmail || user.displayName)) {
          window.electronAPI.setUserIdentity({
            email: identityEmail || null,
            displayName: user.displayName || user.name || (identityEmail ? identityEmail.split('@')[0] : null),
          });
        }
      } catch (_) {}

      // If a user exists, fetch their settings (support Electron custom-token flow where email may be null)
      const fetchUserSettings = async () => {
        try {
          const fallbackEmail = (typeof window !== 'undefined') ? localStorage.getItem('electronEmail') : null;
          const emailForSettings = user.email || fallbackEmail;
          if (!emailForSettings) {
            console.warn('[AuthProvider] No email available for user settings fetch');
            setUserSettings(null);
            return;
          }
          const settings = await firebaseService.getUserSettings(emailForSettings);
          setUserSettings(settings);
        } catch (error) {
          console.error("Error fetching user settings:", error);
          setUserSettings(null);
        }
      };

      fetchUserSettings();
    } else {
      // If there is no user, ensure all user-specific data is cleared
      setUserSettings(null);
      setGcalAccessToken(null);
      setGcalRefreshToken(null);
      setUserCalendars([]);
      setUserSelectedCalendar("");
    }
  }, [user]); // This effect correctly depends only on the user object

  // --- END OF STABLE HOOKS ---

  // handleLogin now simply handles the sign-in, and lets the useEffects do the rest
  const handleLogin = async () => {
    console.log('[AuthProvider] handleLogin called');
    
    if (isElectron()) {
      console.log('[AuthProvider] Electron detected, using Admin SDK to get custom token');
      console.log('[AuthProvider] window.electronAPI available:', !!window.electronAPI);
      console.log('[AuthProvider] window.electronAPI.loginWithGoogle available:', !!window.electronAPI?.loginWithGoogle);

      try {
        console.log('[AuthProvider] Calling window.electronAPI.loginWithGoogle()...');
        const result = await window.electronAPI.loginWithGoogle();
        console.log('[AuthProvider] Main process returned:', result);

        if (result?.customToken) {
          await signInWithCustomToken(auth, result.customToken);
          // Persist email from main process for cases where Firebase user has no email populated from custom token
          if (result?.email && typeof window !== 'undefined') {
            localStorage.setItem('electronEmail', result.email);
          }
          toast({ message: "Successfully signed in!", color: "success" });
        } else {
          console.error('[AuthProvider] Missing custom token from main process');
          toast({ message: "Authentication failed: No token received.", color: "danger" });
        }
      } catch (error) {
        console.error('[AuthProvider] Error in Electron login:', error);
        toast({ message: "Authentication failed. Please try again.", color: "danger" });
      }
      return;
    }
    
    console.log('[AuthProvider] Web detected, using signInWithPopup');
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('https://www.googleapis.com/auth/calendar');
      const result = await signInWithPopup(auth, provider);
      console.log('[AuthProvider] Web sign-in successful:', result);
    } catch (error) {
      console.error('[AuthProvider] Web sign-in failed:', error);
      toast({ message: "Sign-in failed. Please try again.", color: "danger" });
    }
  };

  // --- ALL OF YOUR OTHER FUNCTIONS, RESTORED AND UNCHANGED ---

  const revokeAccess = async () => {
    try {
      const { httpsCallable, functions } = await import('../Utils/firebaseConfig.js');
      const revoke = httpsCallable(functions, "revokeAppAccess");
      await revoke();
      if (user) {
        await firebaseService.updateUserSettings(user.email, {
          googleCalendarConnected: false,
          refreshToken: null
        });
      }
    } catch (error) {
      console.error("Error revoking access:", error);
    }
  };

  const updateSelectedCalendar = async (newCalendar) => {
    if (user && newCalendar !== "") {
      try {
        await firebaseService.updateUserSettings(user.email, { userSelectedCalendar: newCalendar });
      } catch (error) {
        console.error("Error updating calendar: ", error);
      }
    }
  };

  const handleLoginWithEmail = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      let message = error.message.replace('Firebase:', '');
      toast({ message: `${message}`, color: 'danger' });
      console.error(error);
    }
  };

  const handleLogout = () => {
    if (isElectron()) {
      localStorage.removeItem('electronUser');
    }
    setUser(null);
    setLoading(false);
    signOut(auth).catch((error) => console.log("Logout Error:", error));
  };
  
  // The complete context value object to be passed to children
  const value = {
    user,
    loading,
    userSettings,
    onLogin: handleLogin,
    handleLoginWithEmail,
    handleLogout,
    gCalAccessToken,
    gCalRefreshToken,
    setGcalAccessToken,
    setGcalRefreshToken,
    userCalendars,
    setUserCalendars,
    userSelectedCalendar,
    setUserSelectedCalendar,
    revokeAccess,
    updateSelectedCalendar
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};